import { Link } from "react-router-dom"
import { motion } from "motion/react"
import { FavoriteMovie } from "../../types/entities/favorite"
import { useCinemaHref } from "../../hooks/useCinemaHref"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type FavoriteItemProps = {
    item: FavoriteMovie
    onRemove: (id: number) => void
    removing: boolean
    index?: number
}

export default function FavoriteItem({ item, onRemove, removing, index = 0 }: FavoriteItemProps) {
    const href = useCinemaHref()
    const filmTo = href(`/afisha/film/${item.id}`)
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.06 }}
            className="rounded-[20px] border border-white/10 bg-white/[0.05] overflow-hidden flex gap-4 p-4">
            {/* Poster */}
            <Link to={filmTo} state={{ pageTitle: item.name }} className="shrink-0 w-[72px] rounded-[16px] overflow-hidden aspect-[2/3] bg-white/10">
                <img
                    src={item.poster?.big ?? item.poster_mobile?.big}
                    alt={item.name}
                    className="w-full h-full object-cover"
                />
            </Link>

            {/* Info */}
            <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                <div>
                    <Link to={filmTo} state={{ pageTitle: item.name }}>
                        <p
                            className="text-white text-[15px] leading-snug mb-1 transition-colors hover:text-[#349be6]"
                            style={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: "-0.01em" }}>
                            {item.name}
                        </p>
                    </Link>
                </div>

                <div className="flex items-center gap-2 mt-2">
                    {item.age && (
                        <span
                            className="rounded-full border border-white/20 px-2 py-0.5 text-[11px] text-white/50"
                            style={{ fontFamily: MONO, fontWeight: 600 }}>
                            {item.age}
                        </span>
                    )}
                    {item.info && (
                        <span
                            className="rounded-full border border-[#349be6]/30 bg-[#349be6]/10 px-2 py-0.5 text-[11px] text-[#349be6]"
                            style={{ fontFamily: BODY, fontWeight: 600 }}>
                            {item.info}
                        </span>
                    )}
                </div>
            </div>

            {/* Remove button */}
            <div className="shrink-0 flex items-start pt-0.5">
                <motion.button
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    type="button"
                    disabled={removing}
                    onClick={() => onRemove(item.id)}
                    className="size-9 rounded-full border border-[#e4206b]/40 bg-[#e4206b]/10 flex items-center justify-center text-[15px] hover:bg-[#e4206b]/25 transition-colors disabled:opacity-50"
                    title="Убрать из избранного">
                    {removing ? "..." : "❤️"}
                </motion.button>
            </div>
        </motion.div>
    )
}
